import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articleData } from '@/mocks/articles';
import { comics } from '@/mocks/comics';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const [page, setPage] = useState(1);
  const perPage = 15;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  const articleId = Number(id);
  const article = articleData[articleId];

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 text-center">
          <div className="max-w-screen-2xl mx-auto px-4 py-20">
            <h1 className="text-xl font-heading font-bold text-foreground-950 mb-4">漫画未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">您访问的漫画不存在或已被移除</p>
            <Link to="/" className="inline-block px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-all whitespace-nowrap">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const chaps = article.chapters;
  const totalPages = Math.ceil(chaps.length / perPage);
  const start = (page - 1) * perPage;
  const pagedChapters = chaps.slice(start, start + perPage);

  const breadcrumbItems = [
    { name: '首页', href: '/' },
    { name: `${article.categoryName}韩漫`, href: `/category/${article.category}` },
    { name: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    'headline': `${article.title} - 韩国漫画免费在线观看`,
    'description': article.summary,
    'author': { '@type': 'Person', 'name': article.author },
    'datePublished': article.chapters[article.chapters.length - 1]?.date || '2026-01-01',
    'dateModified': article.chapters[0]?.date || '2026-08-05',
    'image': article.cover,
    'url': `https://www.koreamanhua.com/article/${article.id}`,
    'inLanguage': 'zh-Hans',
    'publisher': {
      '@type': 'Organization',
      'name': '韩国漫画网',
    },
  };

  const faqSchema = article.faqs.length > 0 ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': article.faqs.map((f) => ({
      '@type': 'Question',
      'name': f.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': f.answer,
      },
    })),
  } : null;

  const relatedComics = comics
    .filter(c => c.category === article.category && c.id !== article.id)
    .slice(0, 4);

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-16">
          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            <article className="py-6 md:py-8">
              <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
                <div className="flex-shrink-0">
                  <div className="w-40 md:w-52 aspect-[3/4] rounded-lg overflow-hidden">
                    <img
                      src={article.cover}
                      alt={`${article.title} 封面 - 韩国漫画`}
                      title={`${article.title} - ${article.author} 创作的韩国漫画`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>

                <div className="flex-1 min-w-0">
                  <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-2">
                    {article.title}
                  </h1>
                  <div className="flex flex-wrap items-center gap-3 text-sm text-foreground-500 mb-3">
                    <span><i className="ri-user-line mr-1"></i>{article.author}</span>
                    <span><i className="ri-star-fill text-accent-500 mr-1"></i>{article.rating}</span>
                    <span><i className="ri-eye-line mr-1"></i>{(article.views / 10000).toFixed(0)}万阅读</span>
                    <span className="px-2 py-0.5 bg-accent-100 text-accent-800 rounded-full text-xs font-medium">{article.status}</span>
                    <span className="px-2 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs font-medium">{article.categoryName}</span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {article.tags.map((tag) => (
                      <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2 py-1 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>

                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{article.summary}</p>

                  <div
                    className="prose prose-sm max-w-none text-foreground-700 prose-headings:text-foreground-950 prose-headings:font-heading prose-p:leading-relaxed prose-h2:text-lg prose-h2:font-bold"
                    dangerouslySetInnerHTML={{ __html: article.descriptionHtml }}
                  />
                </div>
              </div>

              <section className="mt-8 md:mt-10">
                <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">
                  章节列表 <span className="text-sm font-normal text-foreground-500">（共{chaps.length}话）</span>
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
                  {pagedChapters.map((ch) => (
                    <div
                      key={ch.id}
                      className="flex items-center justify-between px-4 py-3 bg-background-100 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer group"
                    >
                      <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 truncate">{ch.title}</span>
                      <span className="text-xs text-foreground-400 whitespace-nowrap ml-2">{ch.date}</span>
                    </div>
                  ))}
                </div>

                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-1 mt-6">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    >
                      <i className="ri-arrow-left-s-line"></i>
                    </button>
                    {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
                      let p: number;
                      if (totalPages <= 7) {
                        p = i + 1;
                      } else if (page <= 4) {
                        p = i + 1;
                      } else if (page >= totalPages - 3) {
                        p = totalPages - 6 + i;
                      } else {
                        p = page - 3 + i;
                      }
                      return (
                        <button
                          key={p}
                          onClick={() => setPage(p)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-sm font-medium transition-all ${
                            page === p
                              ? 'bg-primary-500 text-background-50'
                              : 'text-foreground-600 hover:bg-background-100'
                          }`}
                        >
                          {p}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="w-8 h-8 flex items-center justify-center rounded-lg text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                    >
                      <i className="ri-arrow-right-s-line"></i>
                    </button>
                  </div>
                )}
              </section>

              {article.faqs.length > 0 && (
                <section className="mt-8 md:mt-10">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">常见问题</h2>
                  <div className="space-y-2">
                    {article.faqs.map((faq, i) => (
                      <details key={i} className="bg-background-100 rounded-lg p-4 cursor-pointer group">
                        <summary className="text-sm font-medium text-foreground-800 list-none flex items-center justify-between">
                          {faq.question}
                          <i className="ri-add-line group-open:ri-subtract-line transition-all text-foreground-400"></i>
                        </summary>
                        <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                      </details>
                    ))}
                  </div>
                </section>
              )}

              {relatedComics.length > 0 && (
                <section className="mt-8 md:mt-10">
                  <h2 className="text-lg font-heading font-bold text-foreground-950 mb-4">相关推荐</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                    {relatedComics.map((comic) => (
                      <Link
                        key={comic.id}
                        to={`/article/${comic.id}`}
                        className="group bg-background-100 rounded-lg overflow-hidden transition-all hover:bg-background-200/70 cursor-pointer"
                      >
                        <div className="relative w-full aspect-[3/4] overflow-hidden">
                          <img
                            src={comic.cover}
                            alt={`${comic.title} - 韩国漫画`}
                            title={`${comic.title} - 韩国漫画免费在线观看`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                          />
                        </div>
                        <div className="p-2.5">
                          <h3 className="text-xs font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">{comic.title}</h3>
                          <p className="text-[10px] text-foreground-500 mt-0.5">{comic.author} · {comic.rating}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </article>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}